import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/utils/api'

export interface Request {
  id: string
  request_type: string
  title: string
  description?: string
  urgency: 'low' | 'medium' | 'high' | 'urgent'
  status: 'pending' | 'approved' | 'denied' | 'completed'
  created_at: string
  response?: string
  responded_at?: string
  requested_by?: string
  requester_name?: string
}

// Query keys factory
export const requestsKeys = {
  all: ['requests'] as const,
  kidRequests: () => [...requestsKeys.all, 'kid'] as const,
  parentRequests: () => [...requestsKeys.all, 'parent'] as const,
}

/**
 * Fetch kid's own requests
 */
export function useKidRequests() {
  return useQuery({
    queryKey: requestsKeys.kidRequests(),
    queryFn: async () => {
      const data = await apiClient.getKidRequests()
      return (data || []) as Request[]
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  })
}

/**
 * Create a new kid request
 */
export function useCreateKidRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      request_type: string
      title: string
      description?: string
      urgency: 'low' | 'medium' | 'high' | 'urgent'
    }) => {
      return await apiClient.createKidRequest(params)
    },
    onSuccess: () => {
      // Invalidate all request queries
      queryClient.invalidateQueries({ queryKey: requestsKeys.all })
    },
  })
}

/**
 * Update a kid request (parent action - approve/deny/complete)
 */
export function useUpdateKidRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      id: string
      status: 'approved' | 'denied' | 'completed'
      response?: string
    }) => {
      return await apiClient.updateKidRequest(params.id, params.status, params.response)
    },
    onSuccess: () => {
      // Invalidate all request queries
      queryClient.invalidateQueries({ queryKey: requestsKeys.all })
    },
  })
}
